import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Repeat, Plus, Trash2, Play, Pause, FileText, CalendarClock, AlertTriangle, Paperclip, Zap } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { localToday, toLocalYmd } from '@/lib/dateRange';

const CATEGORIES = ['Rent', 'License', 'Insurance', 'Utilities', 'Subscription', 'Maintenance', 'Contract', 'Other'];

const emptyLine = () => ({ account_code: '', account_name: '', debit: 0, credit: 0 });
const emptyForm = () => ({
  description: '',
  category: '',
  frequency: 'monthly',
  branch_id: '',
  start_date: localToday(),
  expiry_date: '',
  total_amount: '',
  installments_per_year: '',
  total_installments: '',
  document_ids: [],
  lines: [emptyLine(), emptyLine()],
});

export default function RecurringEntriesPage() {
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [branches, setBranches] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showSchedule, setShowSchedule] = useState(null); // entry being viewed
  const [upcoming, setUpcoming] = useState(null);
  const [due, setDue] = useState(null);
  const [autoposting, setAutoposting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [eR, bR, dR, uR, dueR] = await Promise.all([
        api.get('/accounting/recurring-entries'),
        api.get('/branches'),
        api.get('/documents').catch(() => ({ data: [] })),
        api.get('/accounting/recurring-entries/upcoming?days=30').catch(() => ({ data: null })),
        api.get('/accounting/recurring-entries/due').catch(() => ({ data: null })),
      ]);
      setEntries(eR.data || []);
      setBranches(bR.data || []);
      setDocuments(dR.data || []);
      setUpcoming(uR.data);
      setDue(dueR.data);
    } catch { toast.error('Failed to load recurring entries'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleRunAutopost = async () => {
    setAutoposting(true);
    try {
      const { data } = await api.post('/accounting/recurring-entries/run-autopost');
      if (data.posted_count > 0) toast.success(`Auto-posted ${data.posted_count} journal ${data.posted_count === 1 ? 'entry' : 'entries'}`);
      else toast.info('Nothing due to post right now');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-post failed'); }
    finally { setAutoposting(false); }
  };

  const handleToggleAuto = async (e) => {
    try { await api.put(`/accounting/recurring-entries/${e.id}`, { auto_post: e.auto_post === false }); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  // iter390b — Honor URL prefill from Reconciliation Center "Set up as recurring".
  // Accepts: ?prefill_pattern=<narrative>&amount=<sr>&category=<x>&frequency=<weekly|monthly|...>
  useEffect(() => {
    const pattern = searchParams.get('prefill_pattern');
    const amountStr = searchParams.get('amount');
    if (!pattern && !amountStr) return;
    const amt = amountStr ? parseFloat(amountStr) : 0;
    const cat = searchParams.get('category') || '';
    const freq = searchParams.get('frequency') || 'monthly';
    setForm(prev => ({
      ...prev,
      description: pattern ? `Bulk: ${pattern}` : prev.description,
      category: cat || prev.category,
      frequency: freq,
      total_amount: amt > 0 ? String(amt) : prev.total_amount,
      installments_per_year: freq === 'weekly' ? 52 : freq === 'bi-weekly' ? 26 : freq === 'monthly' ? 12 : freq === 'quarterly' ? 4 : '',
      total_installments: freq === 'weekly' ? 52 : freq === 'bi-weekly' ? 26 : freq === 'monthly' ? 12 : freq === 'quarterly' ? 4 : '',
    }));
    setShowForm(true);
    toast.success(pattern ? `Pre-filled from pattern "${pattern}"` : 'Pre-filled from URL');
    // Clean the URL so a second click doesn't re-fire
    const next = new URLSearchParams(searchParams);
    next.delete('prefill_pattern');
    next.delete('amount');
    next.delete('category');
    next.delete('frequency');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live-preview the schedule in the form
  const formSchedule = (() => {
    const ipy = Number(form.installments_per_year);
    const ti = Number(form.total_installments);
    if (!ipy || ipy <= 0) return [];
    const count = ti && ti > 0 ? ti : ipy;
    try {
      const base = new Date(form.start_date);
      const period = Math.round(365 / ipy);
      const amt = form.total_amount && ti > 0 ? (Number(form.total_amount) / ti) : (form.total_amount ? Number(form.total_amount) / ipy : null);
      return Array.from({ length: Math.min(count, 12) }, (_, i) => {
        const d = new Date(base);
        d.setDate(d.getDate() + period * i);
        return { installment_no: i + 1, due_date: toLocalYmd(d), amount: amt };
      });
    } catch { return []; }
  })();

  const handleCreate = async () => {
    const td = form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const tc = form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    if (Math.abs(td - tc) > 0.01) { toast.error('Debits must equal Credits'); return; }
    if (!form.description) { toast.error('Description is required'); return; }
    try {
      const payload = {
        ...form,
        total_amount: form.total_amount ? Number(form.total_amount) : null,
        installments_per_year: form.installments_per_year ? Number(form.installments_per_year) : null,
        total_installments: form.total_installments ? Number(form.total_installments) : null,
        expiry_date: form.expiry_date || null,
        branch_id: form.branch_id || null,
      };
      await api.post('/accounting/recurring-entries', payload);
      toast.success('Recurring entry created');
      setShowForm(false);
      setForm(emptyForm());
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleDelete = async (id) => { try { await api.delete(`/accounting/recurring-entries/${id}`); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed to delete'); } };
  const handleGenerate = async (id) => { try { await api.post(`/accounting/recurring-entries/${id}/generate`); toast.success('Journal entry generated'); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate'); } };
  const handleToggle = async (e) => { try { await api.put(`/accounting/recurring-entries/${e.id}`, { is_active: !e.is_active }); fetchAll(); } catch { toast.error('Failed'); } };
  const updateLine = (idx, field, val) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], [field]: field === 'debit' || field === 'credit' ? Number(val) || 0 : val };
    setForm({ ...form, lines });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });
  const removeLine = (idx) => { if (form.lines.length > 2) setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) }); };

  const branchName = (id) => branches.find(b => b.id === id)?.name || '—';

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-info to-primary flex items-center justify-center shadow-lg shadow-info/30">
            <Repeat size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Recurring Entries</h1>
            <p className="text-sm text-muted-foreground">Rent · Licenses · Insurance · Subscriptions — with schedules & expiry alerts</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} data-testid="re-add-btn">
            <Plus size={16} className="mr-1" /> New Recurring Entry
          </Button>
        </div>

        {/* Auto-book due banner */}
        {due && due.total_journal_entries > 0 && (
          <Card className="border-info/40 bg-info/5" data-testid="re-autopost-banner">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <Zap size={16} className="text-info" />
              <p className="text-sm flex-1 min-w-[200px]">
                <strong>{due.total_journal_entries}</strong> journal {due.total_journal_entries === 1 ? 'entry is' : 'entries are'} due to be auto-booked
                across <strong>{due.due_entries.length}</strong> recurring {due.due_entries.length === 1 ? 'fee' : 'fees'}.
                <span className="text-muted-foreground"> Posts automatically every day — or run it now.</span>
              </p>
              <Button size="sm" onClick={handleRunAutopost} disabled={autoposting} data-testid="re-run-autopost-btn">
                <Zap size={14} className="mr-1" /> {autoposting ? 'Posting…' : 'Run Auto-Post Now'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Alerts strip */}
        {upcoming && (upcoming.upcoming_installments.length > 0 || upcoming.expiring_soon.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {upcoming.upcoming_installments.length > 0 && (
              <Card className="border-warning/40 bg-warning/5">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <CalendarClock size={14} className="text-warning" />
                    <p className="text-xs font-semibold text-warning">Due in next 30 days</p>
                    <Badge className="bg-warning/20 text-warning ml-auto">{upcoming.upcoming_installments.length}</Badge>
                  </div>
                  <ul className="space-y-0.5">
                    {upcoming.upcoming_installments.slice(0, 4).map((u, i) => (
                      <li key={i} className="text-[11px] flex justify-between gap-2">
                        <span className="truncate">{u.description}</span>
                        <span className="font-mono text-muted-foreground whitespace-nowrap">{u.due_date} · {u.days_until_due}d</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {upcoming.expiring_soon.length > 0 && (
              <Card className="border-error/40 bg-error/5">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <AlertTriangle size={14} className="text-error" />
                    <p className="text-xs font-semibold text-error">Expiring soon</p>
                    <Badge className="bg-error/20 text-error ml-auto">{upcoming.expiring_soon.length}</Badge>
                  </div>
                  <ul className="space-y-0.5">
                    {upcoming.expiring_soon.slice(0, 4).map((u, i) => (
                      <li key={i} className="text-[11px] flex justify-between gap-2">
                        <span className="truncate">{u.description}</span>
                        <span className="font-mono text-muted-foreground whitespace-nowrap">{u.expiry_date} · {u.days_until_expiry}d</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {showForm && (
          <Card className="border-info/40">
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs">Description *</Label>
                  <Input placeholder="e.g. Municipal License 2026" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="re-description" />
                </div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={form.category || 'none'} onValueChange={v => setForm({ ...form, category: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="re-category"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none"><span className="text-muted-foreground">—</span></SelectItem>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Frequency</Label>
                  <Select value={form.frequency} onValueChange={v => setForm({ ...form, frequency: v })}>
                    <SelectTrigger data-testid="re-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Branch (optional)</Label>
                  <Select value={form.branch_id || 'all'} onValueChange={v => setForm({ ...form, branch_id: v === 'all' ? '' : v })}>
                    <SelectTrigger data-testid="re-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><span className="text-muted-foreground">All branches</span></SelectItem>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="re-start-date" />
                </div>
                <div>
                  <Label className="text-xs">Expiry Date</Label>
                  <Input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} data-testid="re-expiry-date" />
                </div>
                <div>
                  <Label className="text-xs">Link Documents</Label>
                  <Select value="" onValueChange={v => {
                    if (!v) return;
                    if (!form.document_ids.includes(v)) setForm({ ...form, document_ids: [...form.document_ids, v] });
                  }}>
                    <SelectTrigger data-testid="re-doc-link"><SelectValue placeholder={`Attach documents (${form.document_ids.length})`} /></SelectTrigger>
                    <SelectContent>
                      {documents.length === 0 && <div className="text-xs text-muted-foreground px-3 py-2">No documents available. Add one in the Documents module first.</div>}
                      {documents.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name || d.title || d.file_name || d.id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.document_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {form.document_ids.map(id => {
                        const d = documents.find(x => x.id === id);
                        return (
                          <Badge key={id} variant="outline" className="text-[10px] gap-1 pr-1">
                            <Paperclip size={9} /> {d?.name || d?.title || d?.file_name || id.slice(0, 6)}
                            <button type="button" className="ml-0.5 text-muted-foreground hover:text-error" onClick={() => setForm({ ...form, document_ids: form.document_ids.filter(x => x !== id) })}>×</button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Advanced schedule block (for Rent / License / Insurance type items) */}
              <div className="bg-muted/40 border border-border rounded-lg p-3 space-y-3">
                <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <CalendarClock size={12} /> Payment Schedule (optional — for licenses, rent, insurance)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Total Amount (contract value)</Label>
                    <Input type="number" placeholder="e.g. 12000" value={form.total_amount} onChange={e => setForm({ ...form, total_amount: e.target.value })} data-testid="re-total-amount" />
                  </div>
                  <div>
                    <Label className="text-xs">Installments per year</Label>
                    <Input type="number" placeholder="1–12" value={form.installments_per_year} onChange={e => setForm({ ...form, installments_per_year: e.target.value })} data-testid="re-ipy" />
                  </div>
                  <div>
                    <Label className="text-xs">Total installments</Label>
                    <Input type="number" placeholder="e.g. 4" value={form.total_installments} onChange={e => setForm({ ...form, total_installments: e.target.value })} data-testid="re-total-installments" />
                  </div>
                </div>
                {formSchedule.length > 0 && (
                  <div className="bg-background rounded p-2 text-[11px]">
                    <p className="text-muted-foreground mb-1">Preview ({formSchedule.length} upcoming):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {formSchedule.map(s => (
                        <Badge key={s.installment_no} variant="outline" className="gap-1 font-mono">
                          #{s.installment_no} · {s.due_date}{s.amount != null ? ` · ${s.amount.toLocaleString()}` : ''}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Journal Lines</p>
                {form.lines.map((line, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Account Code" value={line.account_code} onChange={e => updateLine(i, 'account_code', e.target.value)} className="w-28" />
                    <Input placeholder="Account Name" value={line.account_name} onChange={e => updateLine(i, 'account_name', e.target.value)} className="flex-1" />
                    <Input type="number" placeholder="Debit" value={line.debit || ''} onChange={e => updateLine(i, 'debit', e.target.value)} className="w-28" />
                    <Input type="number" placeholder="Credit" value={line.credit || ''} onChange={e => updateLine(i, 'credit', e.target.value)} className="w-28" />
                    <Button size="sm" variant="ghost" onClick={() => removeLine(i)} disabled={form.lines.length <= 2}><Trash2 size={14} /></Button>
                  </div>
                ))}
                <div className="flex justify-between items-center">
                  <Button size="sm" variant="outline" onClick={addLine}>+ Add Line</Button>
                  <div className="text-xs font-mono">
                    Debit: {form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0).toLocaleString()} |
                    Credit: {form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowForm(false); setForm(emptyForm()); }}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="re-create-btn">Create</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-info border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="re-table">
                <thead>
                  <tr className="border-b bg-muted/50 text-muted-foreground">
                    <th className="text-left p-3 font-medium">Description</th>
                    <th className="text-center p-3 font-medium">Category</th>
                    <th className="text-center p-3 font-medium">Branch</th>
                    <th className="text-center p-3 font-medium">Frequency</th>
                    <th className="text-center p-3 font-medium">Next Due</th>
                    <th className="text-center p-3 font-medium">Expiry</th>
                    <th className="text-right p-3 font-medium">Amount</th>
                    <th className="text-center p-3 font-medium">Docs</th>
                    <th className="text-center p-3 font-medium">Auto</th>
                    <th className="text-center p-3 font-medium">Status</th>
                    <th className="text-center p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">No recurring entries configured</td></tr>}
                  {entries.map(e => (
                    <tr key={e.id} className="border-b hover:bg-muted/50" data-testid={`re-row-${e.id}`}>
                      <td className="p-3">
                        <button className="font-medium text-left hover:text-info" onClick={() => setShowSchedule(e)}>{e.description}</button>
                      </td>
                      <td className="p-3 text-center">{e.category ? <Badge variant="outline" className="text-[10px]">{e.category}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="p-3 text-center text-xs text-muted-foreground">{e.branch_id ? branchName(e.branch_id) : <span className="italic">all</span>}</td>
                      <td className="p-3 text-center"><Badge variant="outline" className="capitalize text-xs">{e.frequency}</Badge></td>
                      <td className="p-3 text-center text-xs">
                        {e.next_installment ? (
                          <span className={e.days_until_next_installment <= 7 ? 'text-warning font-semibold' : 'text-muted-foreground'}>
                            {e.next_installment.due_date}
                          </span>
                        ) : <span className="text-muted-foreground">{e.next_run || '—'}</span>}
                      </td>
                      <td className="p-3 text-center text-xs">
                        {e.expiry_date ? (
                          <span className={e.days_until_expiry != null && e.days_until_expiry <= 30 ? 'text-error font-semibold' : 'text-muted-foreground'}>
                            {e.expiry_date}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="p-3 text-right font-mono"><RiyalIcon size="0.7em" /> {e.total_debit?.toLocaleString() || 0}</td>
                      <td className="p-3 text-center">{e.document_ids?.length ? <Badge variant="outline" className="text-[10px] gap-1"><Paperclip size={10} />{e.document_ids.length}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="p-3 text-center">
                        <button onClick={() => handleToggleAuto(e)} title={e.auto_post === false ? 'Auto-book OFF — click to enable' : 'Auto-book ON — click to disable'} data-testid={`re-auto-toggle-${e.id}`}>
                          <Badge className={`text-[10px] gap-1 cursor-pointer ${e.auto_post === false ? 'bg-muted-foreground' : 'bg-info'}`}>
                            <Zap size={10} />{e.auto_post === false ? 'Off' : 'On'}
                          </Badge>
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <Badge className={`text-xs ${e.is_active ? 'bg-success' : 'bg-muted-foreground'}`}>{e.is_active ? 'Active' : 'Paused'}</Badge>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="outline" onClick={() => handleGenerate(e.id)} title="Generate now" data-testid={`re-generate-${e.id}`}><Play size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleToggle(e)} title={e.is_active ? 'Pause' : 'Resume'}>{e.is_active ? <Pause size={14} /> : <Play size={14} />}</Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(e.id)} data-testid={`re-delete-${e.id}`}><Trash2 size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Schedule / details dialog */}
        <Dialog open={!!showSchedule} onOpenChange={(open) => !open && setShowSchedule(null)}>
          <DialogContent className="max-w-3xl" data-testid="re-schedule-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2">
                <CalendarClock size={16} className="text-info" /> {showSchedule?.description}
              </DialogTitle>
            </DialogHeader>
            {showSchedule && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="bg-muted/40 rounded p-2"><span className="text-muted-foreground block">Category</span><strong>{showSchedule.category || '—'}</strong></div>
                  <div className="bg-muted/40 rounded p-2"><span className="text-muted-foreground block">Branch</span><strong>{showSchedule.branch_id ? branchName(showSchedule.branch_id) : 'All'}</strong></div>
                  <div className="bg-muted/40 rounded p-2"><span className="text-muted-foreground block">Total</span><strong>{showSchedule.total_amount != null ? showSchedule.total_amount.toLocaleString() : '—'}</strong></div>
                  <div className="bg-muted/40 rounded p-2"><span className="text-muted-foreground block">Expiry</span><strong>{showSchedule.expiry_date || '—'}</strong></div>
                </div>
                {showSchedule.document_ids?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Paperclip size={11} /> Linked Documents</p>
                    <div className="flex flex-wrap gap-1">
                      {showSchedule.document_ids.map(id => {
                        const d = documents.find(x => x.id === id);
                        return <Badge key={id} variant="outline" className="text-[10px] gap-1"><FileText size={10} />{d?.name || d?.title || d?.file_name || id.slice(0, 8)}</Badge>;
                      })}
                    </div>
                  </div>
                )}
                {showSchedule.schedule?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">#</th>
                          <th className="text-left p-2">Due Date</th>
                          <th className="text-right p-2">Amount</th>
                          <th className="text-center p-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {showSchedule.schedule.map(s => (
                          <tr key={s.installment_no} className="border-t">
                            <td className="p-2 font-mono">{s.installment_no}</td>
                            <td className="p-2">{s.due_date}</td>
                            <td className="p-2 text-right font-mono">{s.amount != null ? s.amount.toLocaleString() : '—'}</td>
                            <td className="p-2 text-center"><Badge variant="outline" className="text-[10px] capitalize">{s.status}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No installment schedule — add Total Amount + Installments per year to enable.</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
